import { createContext, useContext, useState, useRef, useEffect, type ReactNode } from 'react'
import { Link, useLocation, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, LogOut, MessageCircle, X, Send } from 'lucide-react'
import { portalSettingsApi, type PortalSettings, DEFAULT_SETTINGS } from '@/api/portalSettings'
import { useAppStore } from '@/store/app'
import { usePortalChat } from '@/hooks/usePortalChat'
import { MessageStatusIcon } from '@/components/common/MessageStatusIcon'

export const PortalContext = createContext<PortalSettings>(DEFAULT_SETTINGS)
export const usePortal = () => useContext(PortalContext)

// Carries the logout action down to PortalNav, which sits deep inside
// PortalLayout's children — separate from PortalContext since that's settings
// data, not auth actions.
export const PortalAuthContext = createContext<{ logout: () => void }>({ logout: () => {} })
export const usePortalAuthActions = () => useContext(PortalAuthContext)

// Lets a page-specific chat surface (e.g. a ticket's "chat with assigned
// agent" popup) suppress the general "chat with support" bubble below it
// while it's relevant, so a ticket page never shows two overlapping floating
// chat buttons at once — set from inside `children`, consumed by
// PortalLayout itself, which is why this can't just be a prop on PortalLayout
// (the decision depends on data, like the ticket's assignee, that's only
// fetched by a descendant page, not by PortalLayout's own caller).
const PortalChatVisibilityContext = createContext<{ setHideGlobalChatBubble: (hide: boolean) => void }>({ setHideGlobalChatBubble: () => {} })
export const usePortalChatVisibility = () => useContext(PortalChatVisibilityContext)

export const TEMPLATE_COLORS: Record<string, { bg: string; text: string; ring: string }> = {
  blue:   { bg: 'bg-blue-50',   text: 'text-blue-600',   ring: 'ring-blue-200' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600', ring: 'ring-violet-200' },
  green:  { bg: 'bg-emerald-50',text: 'text-emerald-600',ring: 'ring-emerald-200' },
  amber:  { bg: 'bg-amber-50',  text: 'text-amber-600',  ring: 'ring-amber-200' },
  red:    { bg: 'bg-red-50',    text: 'text-red-600',    ring: 'ring-red-200' },
  teal:   { bg: 'bg-teal-50',   text: 'text-teal-600',   ring: 'ring-teal-200' },
  slate:  { bg: 'bg-slate-100', text: 'text-slate-600',  ring: 'ring-slate-200' },
}

export default function PortalLayout({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ['portal-settings'],
    queryFn: portalSettingsApi.get,
    staleTime: 60_000,
  })
  const settings = data ?? DEFAULT_SETTINGS
  const [hideGlobalChatBubble, setHideGlobalChatBubble] = useState(false)

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8fafc' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: DEFAULT_SETTINGS.branding.primaryColor }} />
      </div>
    )
  }

  return (
    <PortalContext.Provider value={settings}>
      <div
        className="min-h-screen font-sans antialiased"
        style={{
          '--p': settings.branding.primaryColor,
          '--a': settings.branding.accentColor,
          background: '#f1f5f9',
        } as React.CSSProperties}
      >
        <PortalChatVisibilityContext.Provider value={{ setHideGlobalChatBubble }}>
          {children}
        </PortalChatVisibilityContext.Provider>
        {settings.chatBeforeTicketEnabled && !hideGlobalChatBubble && <PortalChatBubble />}
      </div>
    </PortalContext.Provider>
  )
}

// Mounted once by App.tsx's portal route tree (a layout route wrapping every
// /portal/* child with <Outlet/>) instead of each page wrapping itself in
// <PortalLayout> — previously every in-portal navigation unmounted and
// remounted the whole shell (nav bar included), which both felt like a full
// page reload and meant the header below (PortalPageHeader) had no
// persistent element to keep a consistent height against between pages.
export function PortalLayoutRoute() {
  return <PortalLayout><Outlet /></PortalLayout>
}

// ── Shared page header — fixed height on every portal screen ──────────────────
// Every slot (breadcrumb, subtitle) reserves its height whether or not that
// page supplies content for it, so the header never changes size between
// pages — that's what was causing the "zig-zag" jump in the blue band when
// navigating, on top of every page hand-rolling its own hero with a
// different minHeight/padding. Deliberately has no decorative blob/wave SVG
// (the previous per-page heroes did) since those were the other source of
// page-dependent height; the brand gradient + nav are kept exactly as before.
export function PortalPageHeader({ breadcrumb, icon, title, subtitle }: {
  breadcrumb?: ReactNode
  icon?: ReactNode
  title: string
  subtitle?: ReactNode
}) {
  const settings = usePortal()
  const grad = `linear-gradient(135deg, ${settings.branding.primaryColor} 0%, ${settings.branding.accentColor} 100%)`

  return (
    <div style={{ background: grad }}>
      <PortalNav dark />
      <div className="max-w-5xl mx-auto px-6 md:px-10 pb-7">
        <div className="h-5 mb-2 flex items-center">{breadcrumb}</div>
        <div className="flex items-center gap-4">
          {icon}
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight truncate">{title}</h1>
            <p className="text-white/70 text-sm mt-1 min-h-[20px]">{subtitle}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Mode 1: pre-ticket chat with any available agent ──────────────────────────

function PortalChatBubble() {
  const settings = usePortal()
  const { user } = useAppStore()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [answeredPrechat, setAnsweredPrechat] = useState<Set<string | number>>(new Set())
  const listRef = useRef<HTMLDivElement>(null)
  const { messages, sendMessage, connected, markSeen, resolved } = usePortalChat({ recipientId: 'support', enabled: open })

  const answerPrechat = (key: string | number, option: string) => {
    setAnsweredPrechat(prev => new Set(prev).add(key))
    sendMessage(option)
  }

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [messages])

  useEffect(() => {
    if (open) markSeen()
  }, [open, messages.length, markSeen])

  if (!user) return null

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!draft.trim()) return
    sendMessage(draft)
    setDraft('')
  }

  return (
    <div className="fixed bottom-5 right-5 z-50">
      {open && (
        <div className="mb-3 w-80 h-[28rem] rounded-2xl border border-gray-200 bg-white shadow-xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100" style={{ background: settings.branding.primaryColor }}>
            <p className="text-sm font-semibold text-white">Chat with support</p>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {messages.length === 0 && (
              <p className="text-xs text-gray-400 text-center mt-4">
                {connected ? 'Send a message to start chatting with an agent.' : 'Connecting…'}
              </p>
            )}
            {messages.map((m, i) => m.direction === 'system' ? (
              <p key={m.id ?? i} className="text-xs text-gray-400 text-center px-4 py-1">{m.content}</p>
            ) : (
              <div key={m.id ?? i} className={`flex flex-col ${m.direction === 'outgoing' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`max-w-[80%] rounded-xl px-3 py-2 text-sm flex items-end gap-1.5 ${
                    m.direction === 'outgoing' ? 'text-white' : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                  style={m.direction === 'outgoing' ? { background: settings.branding.primaryColor } : {}}
                >
                  <span>{m.content}</span>
                  {m.direction === 'outgoing' && <MessageStatusIcon status={m.status} />}
                </div>
                {!!m.options?.length && !answeredPrechat.has(m.id ?? i) && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 max-w-[80%]">
                    {m.options.map(opt => (
                      <button
                        key={opt}
                        onClick={() => answerPrechat(m.id ?? i, opt)}
                        className="px-3 py-1 rounded-full text-xs font-medium border transition-colors hover:opacity-80"
                        style={{ borderColor: settings.branding.primaryColor, color: settings.branding.primaryColor }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={submit} className="flex items-center gap-2 p-2.5 border-t border-gray-100">
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              disabled={resolved}
              placeholder={resolved ? 'This conversation is resolved' : 'Type a message…'}
              className="flex-1 h-9 px-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
            />
            <button type="submit" disabled={resolved} className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: settings.branding.primaryColor }}>
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white hover:opacity-90 transition-opacity"
        style={{ background: settings.branding.primaryColor }}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </div>
  )
}

// ── Shared nav — rendered INSIDE the hero so the background flows through it ──

export function PortalNav({ dark = true }: { dark?: boolean }) {
  const settings = usePortal()
  const { user } = useAppStore()
  const { logout } = usePortalAuthActions()
  const location = useLocation()
  const isHome = location.pathname === '/portal'
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const text = dark ? 'text-white' : 'text-gray-800'
  const muted = dark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-800'

  const [logoFailed, setLogoFailed] = useState(false)
  useEffect(() => { setLogoFailed(false) }, [settings.branding.logoUrl])

  return (
    <div className="relative z-20 flex items-center justify-between px-6 md:px-10 py-5">
      {/* Logo / brand — falls back to the MultiAccess logo when the org hasn't set
          its own, or when its custom logo fails to load (broken/unreachable URL) */}
      <Link to="/portal" className="flex items-center gap-2.5 flex-shrink-0">
        {settings.branding.logoUrl && !logoFailed ? (
          <img src={settings.branding.logoUrl} alt={settings.branding.companyName}
            onError={() => setLogoFailed(true)}
            className={`h-8 object-contain ${dark ? 'brightness-0 invert' : ''}`} />
        ) : (
          <>
            <img src="/logo.png" alt="MultiAccess" className={`h-7 w-7 object-contain ${dark ? 'brightness-0 invert' : ''}`} />
            <span className={`text-[17px] font-bold tracking-tight ${text}`}>
              {settings.branding.companyName}
            </span>
          </>
        )}
      </Link>

      {/* Right side links */}
      <div className="flex items-center gap-6">
        {!isHome && (
          <Link to="/portal" className={`text-[13px] font-medium ${muted} transition-colors`}>Home</Link>
        )}
        <Link to="/portal/help" className={`text-[13px] font-medium ${muted} transition-colors`}>Help</Link>
        <Link to="/portal/requests" className={`text-[13px] font-medium ${muted} transition-colors`}>My requests</Link>
        <Link
          to="/portal/submit"
          className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-all shadow-sm ${dark ? 'bg-white/15 hover:bg-white/25 text-white border border-white/20' : 'text-white border border-transparent hover:opacity-90'}`}
          style={dark ? {} : { background: settings.branding.primaryColor }}
        >
          Submit a request
        </Link>
        {user && (
          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(v => !v)}
              className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ring-2 transition-opacity hover:opacity-80 ${dark ? 'bg-white/20 text-white ring-white/30' : 'bg-gray-100 text-gray-700 ring-gray-200'}`}
            >
              {(user.name ?? user.email ?? '?')[0].toUpperCase()}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden z-50 text-left">
                <div className="px-3 py-2.5 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-800 truncate">{user.name ?? 'Account'}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email ?? ''}</p>
                </div>
                <div className="p-1">
                  <button
                    onClick={() => { setMenuOpen(false); logout() }}
                    className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
